import { useTranslation } from 'react-i18next';
import { ExternalLink, ArrowUp, Mail, Heart } from 'lucide-react';
import { categories } from '@/mocks/data';

export default function Footer() {
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-foreground-950 text-foreground-200">
      {/* Top CTA Bar */}
      <div className="border-b border-foreground-800/50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-2">
                {t('final_cta_title')}
              </h3>
              <p className="text-foreground-400 text-sm md:text-base max-w-xl">
                {t('final_cta_desc')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 text-sm font-semibold text-foreground-950 bg-background-50 hover:bg-background-100 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {t('cta_spreadsheet')}
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 text-sm font-semibold text-background-50 border border-foreground-700 hover:border-foreground-500 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {t('cta_buy_now')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <a href="/" className="inline-block mb-4">
              <span className="text-lg font-bold text-background-50 tracking-tight">
                AllChinaBuy<span className="text-primary-400">Spread</span>
              </span>
            </a>
            <p className="text-foreground-400 text-sm leading-relaxed mb-6">
              {t('footer_tagline')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-800/50 hover:bg-foreground-700 text-foreground-300 hover:text-background-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
              {t('footer_resources')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="/" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_spreadsheet')}
                </a>
              </li>
              <li>
                <a href="/guides" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_guides')}
                </a>
              </li>
              <li>
                <a href="/reviews" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_reviews')}
                </a>
              </li>
              {categories.slice(0, 5).map((cat, idx) => (
                <li key={idx}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-background-50 transition-colors flex items-center gap-1"
                  >
                    {cat.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
              {t('footer_legal')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="/about" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_about')}
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_privacy')}
                </a>
              </li>
              <li>
                <a href="/terms" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_terms')}
                </a>
              </li>
              <li>
                <a href="/disclaimer" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_disclaimer')}
                </a>
              </li>
              <li>
                <a href="/contact" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('footer_contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
              {t('footer_contact')}
            </h4>
            <div className="space-y-3">
              <a
                href="mailto:contact@allchinabuyspread.fit"
                className="flex items-center gap-2 text-sm text-foreground-400 hover:text-background-50 transition-colors"
              >
                <Mail className="w-4 h-4" />
                contact@allchinabuyspread.fit
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 text-sm text-foreground-400 hover:text-background-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                kaspreadsheet.com
              </a>
            </div>
            <div className="mt-6 p-3 rounded-lg bg-foreground-900/50 border border-foreground-800/50">
              <p className="text-xs text-foreground-500 leading-relaxed">
                {t('footer_independent')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-foreground-800/50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-500">
              {t('footer_copyright')}
            </p>
            <div className="flex items-center gap-1 text-xs text-foreground-500">
              <span>Made with</span>
              <Heart className="w-3 h-3 text-primary-400 fill-primary-400" />
              <span>for smarter buying</span>
            </div>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-background-50 transition-colors"
            >
              {t('scroll_to_top')}
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}